import { subMonths, startOfMonth, endOfMonth, differenceInMonths, addMonths, isSameMonth } from "date-fns";
import { axios } from "utilities/axios";
import {
    AnomalyEventType,
    IAnomaliesCountGetResponse,
    IAnomaliesGetQueryParameters,
    ModelType,
    TimePeriodGroupBy,
} from "@edgetier/watchtower-types";
import Qs from "qs";
import { parseUrlDateTime } from "utilities/parse-url-date-time";

// By default, how many months are displayed by the filter.
export const FILTER_MONTH_RANGE = 3;

/**
 * Count the number of anomalies in the range.
 * @param timeZone User's time zone.
 * @param url      API URL.
 * @returns        Promise of anomaly counts.
 */
export const getFilterData = async (url: string, [startDateTime, endDateTime]: Date[], timeZone: string) => {
    const requestParameters: IAnomaliesGetQueryParameters = {
        anomalyEventType: AnomalyEventType.New,
        groupBy: TimePeriodGroupBy.Days,
        startDateTime,
        endDateTime,
        modelType: [ModelType.Metric, ModelType.Text],
        timeZone,
    };

    const response = await axios.get<IAnomaliesCountGetResponse>(url, { params: requestParameters });
    return response.data.items;
};

/**
 * Get the default filter range.
 * @param parameters The URL parameters.
 * @returns Start and end date time tuple.
 */
export const getInitialDateRange = (parameters: Qs.ParsedQs): [Date, Date] => {
    const today = new Date();

    const defaultStartDateTime = subMonths(startOfMonth(today), FILTER_MONTH_RANGE - 1);
    const defaultEndDateTime = endOfMonth(today);

    const startDateTime = startOfMonth(parseUrlDateTime(parameters.startDateTime, defaultStartDateTime));
    const endDateTime = endOfMonth(parseUrlDateTime(parameters.endDateTime, defaultEndDateTime));

    const monthDifference = differenceInMonths(endDateTime, startDateTime);
    const minDifference = FILTER_MONTH_RANGE - 1;

    // Always show the number of months specified by FILTER_MONTH_RANGE.
    if (monthDifference < minDifference) {
        const monthsToAdd = minDifference - monthDifference;
        // Don't add a month into the future.
        if (isSameMonth(endDateTime, today)) {
            return [subMonths(startDateTime, monthsToAdd), endDateTime];
        }
        return [startDateTime, addMonths(endDateTime, monthsToAdd)];
    }

    return [startDateTime, endDateTime];
};
